// Iter 111.1 · Super Admin Override Session admin.
//
// Distinct from the tenant Migration Mode: a session-based, auto-
// expiring transactional override that the Primary Super Admin can
// activate to unblock genuine controlled scenarios (backdating payroll
// structures, PT master rules, compliance docs, etc.). Every write
// done under an active session is tagged with the session id.

import { useCallback, useEffect, useState } from "react";
import { Shield, Lock, LockOpen, Warning, ClockCountdown, ListChecks, User } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

function formatSeconds(s) {
    if (s == null || s <= 0) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}

export default function SuperAdminOverrideAdmin() {
    const { user } = useAuth();
    // `is_primary_admin` is the field returned by /auth/me (Iter 51.2 governance lock).
    const isPrimarySuperAdmin = user?.is_primary_admin === true;
    const [scopes, setScopes] = useState([]);
    const [defaultDurationMin, setDefaultDurationMin] = useState(60);
    const [maxDurationMin, setMaxDurationMin] = useState(240);
    const [active, setActive] = useState(null);
    const [history, setHistory] = useState([]);
    // Form state
    const [selectedScopes, setSelectedScopes] = useState({});
    const [reason, setReason] = useState("");
    const [duration, setDuration] = useState(60);
    const [saving, setSaving] = useState(false);
    const [endNote, setEndNote] = useState("");
    // Iter 111.1 UAT · Obs 6 — Confirmation modal capturing operator
    // Name / Role / local Date & Time before activation. Backend audit
    // remains the source of truth (log_audit captures the same fields
    // server-side) — this is a UX confirmation surface, not a trust
    // boundary.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmSnapshot, setConfirmSnapshot] = useState(null);
    // Countdown
    const [tick, setTick] = useState(0);

    const reload = useCallback(async () => {
        try {
            const [scopesRes, activeRes, historyRes] = await Promise.all([
                api.get("/admin/override/scopes"),
                api.get("/admin/override/active"),
                api.get("/admin/override/history").catch(() => ({ data: { items: [] } })),
            ]);
            setScopes(scopesRes.data.scopes || []);
            setDefaultDurationMin(scopesRes.data.default_duration_minutes || 60);
            setMaxDurationMin(scopesRes.data.max_duration_minutes || 240);
            setActive(activeRes.data.session);
            setHistory(historyRes.data.items || []);
            setDuration(scopesRes.data.default_duration_minutes || 60);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load override state"));
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // Local ticker so seconds_remaining decays visually.
    useEffect(() => {
        if (!active) return;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [active]);
    const secondsRemaining = active ? Math.max(0, (active.seconds_remaining || 0) - tick) : 0;
    // Auto-refresh when the session expires
    useEffect(() => {
        if (active && secondsRemaining === 0 && tick > 0) reload();
    }, [active, secondsRemaining, tick, reload]);

    const toggleScope = (key) => setSelectedScopes((s) => ({ ...s, [key]: !s[key] }));
    const anyScopeSelected = Object.values(selectedScopes).some(Boolean);

    const openConfirm = () => {
        const chosen = Object.keys(selectedScopes).filter((k) => selectedScopes[k]);
        if (chosen.length === 0) { toast.error("Select at least one scope"); return; }
        if (reason.trim().length < 20) { toast.error("Reason must be at least 20 characters"); return; }
        setConfirmSnapshot({
            operator_name: user?.name || user?.email || "Unknown Operator",
            operator_email: user?.email || "unknown",
            operator_role: user?.role || "unknown",
            is_primary_admin: !!user?.is_primary_admin,
            local_datetime: new Date().toLocaleString(undefined, {
                weekday: "short", year: "numeric", month: "short", day: "2-digit",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
            }),
            iso_datetime: new Date().toISOString(),
            scopes: chosen,
            reason: reason.trim(),
            duration_minutes: duration,
        });
        setConfirmOpen(true);
    };

    const startSession = async () => {
        if (!confirmSnapshot) { openConfirm(); return; }
        setSaving(true);
        try {
            await api.post("/admin/override/start", {
                scopes: confirmSnapshot.scopes,
                reason: confirmSnapshot.reason,
                duration_minutes: confirmSnapshot.duration_minutes,
            });
            toast.success(`Override session activated for ${confirmSnapshot.scopes.length} scope(s)`);
            setSelectedScopes({}); setReason(""); setTick(0);
            setConfirmOpen(false);
            setConfirmSnapshot(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Could not start session"));
        } finally {
            setSaving(false);
        }
    };

    const endSession = async () => {
        if (!window.confirm("End the active override session now?")) return;
        try {
            await api.post("/admin/override/end", { reason: endNote.trim() });
            toast.success("Override session ended");
            setEndNote(""); setTick(0);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Could not end session"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1200px]" data-testid="super-admin-override-page">
            <PageHeader
                eyebrow="Platform Controls"
                title="Super Admin Override"
                description="Time-boxed, audited platform capability for Primary Super Admin to bypass specific backdating guards during controlled migration / correction scenarios. Every write during an active session is tagged with the session id and reason."
            />

            {!isPrimarySuperAdmin && (
                <div className="mt-5 p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="override-no-primary-warning">
                    <Warning size={20} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                        <div className="text-sm font-semibold text-amber-900">Restricted</div>
                        <div className="text-xs text-amber-800 mt-1">Only the Primary Super Admin can activate an override session. Contact your platform owner.</div>
                    </div>
                </div>
            )}

            {/* ── Active session panel ─────────────────────────────── */}
            <section className="mt-5">
                {active ? (
                    <div className="rounded-xl border border-rose-300 bg-rose-50 p-5 flex items-start gap-4" data-testid="override-active-panel">
                        <LockOpen size={28} className="text-rose-600 shrink-0" />
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <span className="text-base font-semibold text-rose-900">Active override session</span>
                                <Badge className="bg-rose-200 text-rose-900 text-[10px]" data-testid="override-active-id">{active.id}</Badge>
                            </div>
                            <div className="mt-1 text-xs text-rose-800">by <b>{active.actor_email}</b> · started {active.started_at?.slice(0, 16).replace("T", " ")}</div>
                            <div className="mt-2 text-sm text-slate-800 italic">&ldquo;{active.reason}&rdquo;</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {(active.scopes || []).map((s) => (
                                    <Badge key={s} variant="outline" className="text-[10px] bg-white" data-testid={`override-active-scope-${s}`}>{s}</Badge>
                                ))}
                            </div>
                            <div className="mt-3 flex items-center gap-4 text-sm">
                                <span className="flex items-center gap-1.5 font-mono">
                                    <ClockCountdown size={16} className="text-rose-700" />
                                    <span data-testid="override-time-remaining">{formatSeconds(secondsRemaining)}</span> remaining
                                </span>
                                <span className="text-slate-500 text-[11px]">auto-expires at {active.expires_at?.slice(11, 19)}</span>
                            </div>
                        </div>
                        {isPrimarySuperAdmin && (
                            <div className="flex flex-col gap-2">
                                <Input placeholder="End reason (optional)" value={endNote} onChange={(e) => setEndNote(e.target.value)} className="w-56 text-sm" data-testid="override-end-note" />
                                <Button variant="destructive" size="sm" onClick={endSession} data-testid="override-end-btn">
                                    <Lock size={14} className="mr-1" /> End Now
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 flex items-center gap-3" data-testid="override-idle-panel">
                        <Lock size={20} className="text-slate-500" />
                        <div className="text-sm text-slate-700">No active override session. All backdating guards enforced normally.</div>
                    </div>
                )}
            </section>

            {/* ── Activation form (Primary Super Admin only) ─────── */}
            {isPrimarySuperAdmin && !active && (
                <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="override-activation-form">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield size={16} className="text-indigo-600" />
                        <h3 className="text-sm font-semibold">Activate a new session</h3>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <Label>Scopes (choose one or more)</Label>
                            <div className="mt-2 space-y-2 border rounded p-3 bg-slate-50">
                                {scopes.map((s) => (
                                    <label key={s.key} className="flex items-start gap-2 text-sm cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={!!selectedScopes[s.key]}
                                            onChange={() => toggleScope(s.key)}
                                            className="mt-1"
                                            data-testid={`override-scope-checkbox-${s.key}`}
                                        />
                                        <span>
                                            <span className="font-medium">{s.label}</span>
                                            <span className="text-[11px] text-slate-500 block">{s.hint}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>Business reason * (min 20 chars)</Label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Migrating July 2025 payroll from legacy system — need to backdate structure to 01-Apr-2025 for the historical FY."
                                rows={3}
                                data-testid="override-reason"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">{reason.trim().length} / 1000 chars · audited verbatim</div>
                        </div>
                        <div>
                            <Label>Duration (minutes · max {maxDurationMin})</Label>
                            <Input
                                type="number" step="1" min="5" max={maxDurationMin}
                                value={duration}
                                onChange={(e) => setDuration(Math.min(maxDurationMin, Math.max(5, Number(e.target.value) || 0)))}
                                className="w-32"
                                data-testid="override-duration"
                            />
                            <div className="text-[11px] text-slate-400 mt-1">Session auto-expires at the specified time. Default {defaultDurationMin}m.</div>
                        </div>
                        <div>
                            <Button
                                onClick={openConfirm}
                                disabled={saving || !anyScopeSelected || reason.trim().length < 20}
                                data-testid="override-start-btn"
                            >
                                <LockOpen size={14} className="mr-1" /> Review & Activate Override Session
                            </Button>
                        </div>
                    </div>
                </section>
            )}

            {/* ── Session history ─────────────────────────────────── */}
            <section className="mt-6" data-testid="override-history-section">
                <div className="flex items-center gap-2 mb-3">
                    <ListChecks size={16} className="text-slate-500" />
                    <h3 className="text-sm font-semibold">Recent Sessions</h3>
                </div>
                <div className="border rounded-xl bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2">Session</th>
                                <th className="text-left px-3 py-2">Actor</th>
                                <th className="text-left px-3 py-2">Scopes</th>
                                <th className="text-left px-3 py-2">Started</th>
                                <th className="text-left px-3 py-2">Ended / Expires</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr><td colSpan="7" className="text-center px-3 py-4 text-slate-400">No session history yet.</td></tr>
                            ) : history.map((h) => (
                                <tr key={h.id} className="border-t border-slate-100" data-testid={`override-history-row-${h.id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px]">{h.id}</td>
                                    <td className="px-3 py-2 text-slate-700">{h.actor_email}</td>
                                    <td className="px-3 py-2"><div className="flex flex-wrap gap-0.5">{(h.scopes || []).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}</div></td>
                                    <td className="px-3 py-2 text-xs">{h.started_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-3 py-2 text-xs">{(h.ended_at || h.expires_at)?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={
                                            h.status === "active" ? "bg-emerald-100 text-emerald-800" :
                                            h.status === "expired" ? "bg-slate-200 text-slate-700" :
                                            h.status === "ended" ? "bg-blue-100 text-blue-800" : "bg-rose-100 text-rose-800"
                                        }>{h.status}</Badge>
                                        {h.end_reason && <div className="text-[10px] text-slate-400 mt-0.5">{h.end_reason}</div>}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500 max-w-md">{h.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* ── Iter 111.1 UAT · Obs 6 — Confirmation modal ─────── */}
            <Dialog open={confirmOpen} onOpenChange={(v) => { if (!v) { setConfirmOpen(false); } }}>
                <DialogContent className="max-w-lg" data-testid="override-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield size={18} className="text-indigo-600" />
                            Confirm override session activation
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            You are about to activate a Super Admin Override session.
                            Every write performed during this window will be
                            audit-tagged with your identity, the session id, and
                            the reason recorded below. Backend audit is the
                            source of truth.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmSnapshot && (
                        <div className="space-y-3 py-1">
                            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3" data-testid="override-confirm-operator">
                                <div className="flex items-center gap-2 mb-2">
                                    <User size={14} className="text-indigo-600" />
                                    <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Operator</span>
                                </div>
                                <dl className="grid grid-cols-3 gap-y-1 text-xs">
                                    <dt className="text-slate-500">Name</dt>
                                    <dd className="col-span-2 font-medium text-slate-900" data-testid="override-confirm-operator-name">
                                        {confirmSnapshot.operator_name}
                                        <span className="text-slate-500 font-normal ml-1">({confirmSnapshot.operator_email})</span>
                                    </dd>
                                    <dt className="text-slate-500">Role</dt>
                                    <dd className="col-span-2 font-medium text-slate-900" data-testid="override-confirm-operator-role">
                                        {confirmSnapshot.operator_role}
                                        {confirmSnapshot.is_primary_admin && (
                                            <Badge className="bg-indigo-200 text-indigo-900 text-[10px] ml-2">Primary Super Admin</Badge>
                                        )}
                                    </dd>
                                    <dt className="text-slate-500">Local Date &amp; Time</dt>
                                    <dd className="col-span-2 font-mono text-slate-900" data-testid="override-confirm-datetime">
                                        {confirmSnapshot.local_datetime}
                                    </dd>
                                </dl>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white p-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Scopes ({confirmSnapshot.scopes.length})</div>
                                <div className="flex flex-wrap gap-1.5" data-testid="override-confirm-scopes">
                                    {confirmSnapshot.scopes.map((s) => (
                                        <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                                    ))}
                                </div>
                                <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Duration</div>
                                <div className="text-xs text-slate-800 font-mono" data-testid="override-confirm-duration">
                                    {confirmSnapshot.duration_minutes} minutes (auto-expires)
                                </div>
                                <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Reason (audited verbatim)</div>
                                <div className="text-xs italic text-slate-700" data-testid="override-confirm-reason">
                                    &ldquo;{confirmSnapshot.reason}&rdquo;
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving} data-testid="override-confirm-cancel">
                            Cancel
                        </Button>
                        <Button onClick={startSession} disabled={saving} data-testid="override-confirm-activate">
                            <LockOpen size={14} className="mr-1" /> {saving ? "Activating…" : "Confirm & Activate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
